import { createLogger } from '@/services/logger/logger';
import * as Sentry from '@sentry/react';
import type { CallSayParams } from '@vonage/client-sdk';
import { useCallback } from 'react';
import { useVonageClient } from '../../vonage-client-provider';
import { serializeError } from '../error-utils';
import { useVoiceCallState } from '../voice-call-container';

export type SayOptions = Omit<CallSayParams, 'text'>;

const log = createLogger('Say', ['calls']);

export const useSay = () => {
  const vonageClient = useVonageClient();
  const [call] = useVoiceCallState();

  const say = useCallback(
    async (text: string, options?: SayOptions) => {
      if (!call.callId) {
        return;
      }
      log.info('Say attempt', {
        callId: call.callId,
        textLength: text?.length,
        timestamp: Date.now(),
      });

      try {
        await vonageClient.say(call.callId, { text, ...options });
      } catch (error) {
        log.error('Say failed', {
          callId: call.callId,
          textLength: text?.length,
          ...serializeError(error),
          timestamp: Date.now(),
        });
        Sentry.captureException(error, {
          tags: {
            component: 'voice_say',
          },
          extra: {
            callId: call.callId,
            textLength: text?.length,
            options,
          },
        });
        throw error;
      }
    },
    [call.callId, vonageClient],
  );

  return { say };
};
